import React from 'react';
import { closedReason, dueOf, isWalkIn } from '../../../lib/eventJourney';
import { MoveToMenu, StageChip, cardSub, shortDate } from './guestUi';

const RSVP_TEXT = { confirmed: 'Yes', declined: 'Declined', cancelled: 'Cancelled' };

export const rsvpText = (card, columnKey) => {
    if (isWalkIn(card)) return 'Walk-in';
    if (RSVP_TEXT[card.rsvp_status]) return RSVP_TEXT[card.rsvp_status];
    return columnKey === 'to_invite' ? '—' : 'No response';
};

export const attendanceText = (card, phase) => {
    if (card.attendance_status === 'attended') return `Attended${card.attendance_method === 'face_match' ? ' · Face match' : ''}`;
    if (card.attendance_status === 'no_show') return 'No-show';
    return phase === 'before' ? '—' : 'Pending';
};

const lastTouch = (card) => (card.last_outreach_at ? shortDate(card.last_outreach_at) : '—');

const DUE_COLOR = { red: 'text-red-700 font-medium', amber: 'text-amber-700 font-medium' };

/** The same rows as the board, as a table: Stage is the one status; RSVP and Attendance are the facts behind it. */
export default function GuestsList({
    columns, cards, columnOf, phase, selectedIds, onToggle, onToggleAll, onOpen, onRequestMove, readOnly,
}) {
    const order = (key) => columns.findIndex((column) => column.key === key);
    const rows = cards.slice().sort((a, b) => order(columnOf(a)) - order(columnOf(b)) || a.name.localeCompare(b.name));
    const allChecked = rows.length > 0 && rows.every((card) => selectedIds.includes(card.contact_id));
    return (
        <div data-testid="guests-list" className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
            <table className="w-full min-w-[960px] text-left text-sm">
                <thead className="bg-stone-50 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                    <tr>
                        <th className="w-10 px-3 py-2.5"><input type="checkbox" aria-label="Select all guests" checked={allChecked} onChange={() => onToggleAll(rows)} className="h-4 w-4 accent-stone-900" /></th>
                        <th className="px-2 py-2.5">Guest</th><th className="px-2 py-2.5">Stage</th><th className="px-2 py-2.5">RSVP</th>
                        <th className="px-2 py-2.5">Attendance</th><th className="px-2 py-2.5">Owner</th><th className="px-2 py-2.5">Next step</th><th className="px-2 py-2.5">Last touch</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                    {rows.map((card) => {
                        const key = columnOf(card);
                        const column = columns.find((item) => item.key === key) || { key, label: key };
                        const due = dueOf(card, key, phase, shortDate);
                        return (
                            <tr key={card.contact_id} data-testid={`guest-row-${card.contact_id}`} data-column={key} onClick={(event) => { if (!event.target.closest('button, input, [role="menu"]')) onOpen(card); }} className="cursor-pointer hover:bg-stone-50">
                                <td className="px-3 py-2"><input type="checkbox" aria-label={`Select ${card.name}`} checked={selectedIds.includes(card.contact_id)} onChange={() => onToggle(card)} className="h-4 w-4 accent-stone-900" /></td>
                                <td className="px-2 py-2">
                                    <button type="button" onClick={() => onOpen(card)} className="block max-w-[240px] truncate text-left font-medium text-stone-900 hover:underline">{card.name}</button>
                                    <span className="block max-w-[240px] truncate text-xs text-stone-500">{cardSub(card)}</span>
                                </td>
                                <td className="px-2 py-2">
                                    <MoveToMenu columns={columns} current={key} disabled={readOnly} label={`Move ${card.name} to…`} testId={`guest-row-move-${card.contact_id}`}
                                        onPick={(target) => onRequestMove([card], target)}
                                        trigger={({ toggle, disabled }) => <StageChip column={column} label={key === 'closed' ? `Closed · ${closedReason(card)}` : column.label} onClick={toggle} disabled={disabled} data-testid={`guest-row-stage-${card.contact_id}`} />} />
                                </td>
                                <td className="px-2 py-2 text-xs text-stone-700">{rsvpText(card, key)}</td>
                                <td className="px-2 py-2 text-xs text-stone-700">{attendanceText(card, phase)}</td>
                                <td className="px-2 py-2 text-xs text-stone-700">{card.owner_name || '—'}</td>
                                <td className={`px-2 py-2 text-xs ${due ? DUE_COLOR[due.tone] || 'text-stone-700' : 'text-stone-700'}`}>{due ? due.text : '—'}</td>
                                <td className="px-2 py-2 text-xs text-stone-500">{lastTouch(card)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {rows.length === 0 && <p className="px-4 py-8 text-center text-sm text-stone-500">No guests match these filters.</p>}
        </div>
    );
}
